import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import _ from "lodash";
import {
  AssignedId,
  TaskAssignmentType,
  WorkflowAssignedId,
  WorkflowAssignmentType,
} from "../../../components/Workflow/FlowElements/FlowElementGrid";
import "../../../components/Workflow/FlowElements/FlowElements.scss";
import FlowElementsAssignedId from "../../../components/Workflow/FlowElements/FlowElementsAssignedId";
import { FlowElementsContext } from "../../../components/Workflow/FlowElements/FlowElementsContextProvider";
import FlowElementsWorkflowAssignedId from "../../../components/Workflow/FlowElements/FlowElementsWorkflowAssignedId";
import { useFlowElementHandlers } from "../../../components/Workflow/FlowElements/flowElementscommonHook";
import "../../../components/Workflow/workflow.scss";
import { edit } from "../../../components/Workflow/workflowHelper";
import { icon, label, number, tooltip } from "../../../config";
import { getActiveMemberVariables } from "../../../shared/services/workflow.services";
import { GridContext } from "../../../utils/kendo";
import { FlowElementCommandCell } from "./FlowElementCommandCell";
import { SELF_ITEM } from "./FlowElementConstants";

/**
 * Parent Component : TaskDetailedFlowElementDetails or WorkflowDetailedFlowElementDetails (conditional)
 * This is the Detailed view for flow elements ( for task only )
 * Children : FlowElementCommandCell
 *  @author Sarthak Arora
 */
const DetailedFlowElementDetails = (props) => {
  const {
    editField,
    taskAssignmentTypes,
    workflowAssignmentTypes,
    handleFlowElementEdit,
    handleNewFlowElement,
    handleDeleteFlowElement,
    isRuleBasedFlow,
  } = props;
  const { task } = useSelector((state) => state.taskSidebar);
  const { user } = useSelector((state) => state.auth);
  const { defaultDetails } = useSelector((state) => state.tasks);
  const [dynamicFlow, setDynamicFlow] = useState(props?.dataItem?.flowElementType === number.TWO || props?.dataItem?.dynamicFlow);
  const [selectedActiveMember, setSelectedActiveMember] = useState(null);
  const [profileType, setProfileType] = useState("currentAssignee");
  const { flowElementData, setFlowElementData, taskElement } = useContext(GridContext);
  const [activeMemberVariables, setActiveMemberVariables] = useState([]);
  const { flowElements } = useSelector((state) => state.taskSidebar);
 
  const {
    flowAssignmentType,
    setFlowAssignmentType,
    flowWorkflowAssignmentType,
    setFlowWorkflowAssignmentType,
    flowTaskAssignmentId,
    setFlowTaskAssignmentId,
    flowWorkflowAssignmentId,
    setFlowWorkflowAssignmentId,
    isApproval,
    setIsApproval,
    isFlowLocked,
    wrapper,
    selection,
    setSelection,
  } = useContext(FlowElementsContext);
  const [previewAdvanced, setPreviewAdvanced] = useState(false);
  const { handleApprovalFlow, handleFlowLock, showTooltip, hideTooltip } = useFlowElementHandlers();

  /**
   * sets isApproval to true if worflow element is in approval
   */
  useEffect(() => {
    setIsApproval(props?.dataItem?.isApproval);
  }, [props?.dataItem?.isApproval]);

  /**
   * Sets profileType based on assignBasedOnCreator value from data
   * assignBasedOnCreator: 1 -> "ticketCreator"
   * assignBasedOnCreator: 0 -> "currentAssignee"
   */
  useEffect(() => {
    if (props?.dataItem?.assignBasedOnCreator === 1) {
      setProfileType("ticketCreator");
    } else if (props?.dataItem?.assignBasedOnCreator === 0) {
      setProfileType("currentAssignee");
    }
  }, [props?.dataItem?.assignBasedOnCreator]);

  useEffect(() => {
    getActiveMemberVariables({ companyId: user.companyId }).then((res) => {
      const variables = profileType === "ticketCreator" && isRuleBasedFlow ? [SELF_ITEM, ...res] : res;
      setActiveMemberVariables(variables);
      let selectedActiveMember = variables?.find((item) => item._id === props?.dataItem?.variableId);
      setSelectedActiveMember(selectedActiveMember || variables?.[number.ZERO]);
    });
  }, [user.companyId, profileType, isRuleBasedFlow]);

  const getPreviewAdvanced = () => {
    if (props?.dataItem?.id === undefined && !props.isRuleBasedFlow) return previewAdvanced; // New Flow Element - show advanced only if explicitly requested
    if (previewAdvanced) return true; // Either advance btn clicked or Flow element Approved or locked
    return Boolean(isApproval) || Boolean(isFlowLocked) || Boolean(dynamicFlow);
  };

  /**
   * Initialize workflow context when switching from Dynamic to Workflows mode
   * This ensures dropdowns are populated even when saved data was dynamic
   */
  const initializeWorkflowContext = () => {
    // Set task assignment type to Project (value 3)
    if (taskAssignmentTypes && !flowAssignmentType) {
      const projectAssignmentType = taskAssignmentTypes.find((t) => t.value === number.THREE);
      if (projectAssignmentType) {
        setFlowAssignmentType(projectAssignmentType);
      }
    }

    // Set task assignment ID to first project
    if (defaultDetails?.allProjectsList && !flowTaskAssignmentId) {
      const allProjectList = _.orderBy([...defaultDetails.allProjectsList], [(project) => project?.label?.toLowerCase()?.trim()], ["asc"]);
      const firstProject = allProjectList?.[number.ZERO];
      if (firstProject) {
        firstProject.AssignmentType = number.THREE;
        setFlowTaskAssignmentId(firstProject);
      }
    }

    // Set workflow assignment type to Workflow (value 5)
    if (workflowAssignmentTypes && !flowWorkflowAssignmentType) {
      const workflowAssignmentType = workflowAssignmentTypes.find((t) => t.value === number.FIVE);
      if (workflowAssignmentType) {
        setFlowWorkflowAssignmentType(workflowAssignmentType);
      }
    }

    // Note: flowWorkflowAssignmentId will be auto-populated by FlowElementsWorkflowAssignedId component
    // when flowTaskAssignmentId is set
  };

  return (
    <div>
      <div className={`workflow-grid-border position-relative ${taskElement ? "px-2" : "px-2"}  ${isApproval && "border wf-element-border"} `}>
        <div>
          <div className={`row wf-element-row`}>
            {dynamicFlow ? (
              <>
                {!props?.dataItem[editField] ? (
                  <div className='col-md-6'>
                    {<label>{label.ASSIGN_TO}</label>}
                    <div className='d-flex align-items-center'>
                      {taskElement && <label className='mr-2 mb-0'>{label.DYNAMIC} Assignment:</label>}
                      <span>{selectedActiveMember?.name}</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={`form-group col-md-6`}>
                      <label className='w-100'>{label.PROFILE}</label>
                      <Button
                        className={`workflow-element-flow-btn ${profileType === "currentAssignee" ? "wf-element-flow-btn-selected" : ""}`}
                        onClick={() => setProfileType("currentAssignee")}
                        selected={profileType === "currentAssignee"}
                        togglable={true}
                        disabled={!props?.dataItem[editField]}
                        size='small'>
                        Current Assignee
                      </Button>
                      <Button
                        className={`ml-2 workflow-element-flow-btn ${profileType === "ticketCreator" ? "wf-element-flow-btn-selected" : ""}`}
                        onClick={() => setProfileType("ticketCreator")}
                        selected={profileType === "ticketCreator"}
                        togglable={true}
                        disabled={!props?.dataItem[editField]}
                        size='small'>
                        Ticket Creator
                      </Button>
                    </div>
                    <div className='form-group col-md-6'>
                      {<label>{label.ASSIGN_TO}</label>}
                      <DropDownList
                        data={activeMemberVariables}
                        textField='name'
                        valueField='id'
                        onChange={(e) => {
                          setSelectedActiveMember(e.value);
                        }}
                        disabled={!props?.dataItem[editField]}
                        value={selectedActiveMember}
                      />
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <div className={`form-group col-md-6`}>
                  {<label>{label.ASSIGN_TO}</label>}
                  <FlowElementsAssignedId
                    {...props}
                    flowTaskAssignmentId={flowTaskAssignmentId}
                    setFlowTaskAssignmentId={setFlowTaskAssignmentId}
                    flowAssignmentType={flowAssignmentType}
                    setFlowAssignmentType={setFlowAssignmentType}
                    setFlowWorkflowAssignmentType={setFlowWorkflowAssignmentType}
                    setFlowWorkflowAssignmentId={setFlowWorkflowAssignmentId}
                    workflowAssignmentTypes={workflowAssignmentTypes}
                    selection={selection}
                    setSelection={setSelection}
                    taskAssignmentTypes={taskAssignmentTypes}
                    flowElementData={flowElementData}
                  />
                </div>
                {flowAssignmentType && (
                  <>
                    <div className={`form-group col-md-6`}>
                      <label>{flowTaskAssignmentId?.AssignmentType === number.TWO ? label.WORKFLOWS : label.USER_WORKFLOWS}</label>
                      <FlowElementsWorkflowAssignedId
                        {...props}
                        flowWorkflowAssignmentId={flowWorkflowAssignmentId}
                        setFlowWorkflowAssignmentId={setFlowWorkflowAssignmentId}
                        flowWorkflowAssignmentType={flowWorkflowAssignmentType}
                        flowTaskAssignmentId={flowTaskAssignmentId}
                        flowAssignmentType={flowAssignmentType}
                        setFlowWorkflowAssignmentType={setFlowWorkflowAssignmentType}
                        workflowAssignmentTypes={workflowAssignmentTypes}
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <div className={`row`}>
            {getPreviewAdvanced() && (
              <>
                <div className={`form-group col-md-6`}>
                  <label className='w-100'>{label.ASSIGNMENT}</label>
                  <Button
                    className={`workflow-element-flow-btn ${!dynamicFlow ? "wf-element-flow-btn-selected" : ""}`}
                    onClick={() => {
                      setDynamicFlow(false);
                      if (!flowTaskAssignmentId ) {
                        initializeWorkflowContext();
                      }
                    }}
                    selected={!dynamicFlow}
                    togglable={true}
                    disabled={!props?.dataItem?.[editField]}
                    size='small'>
                    {icon.WF_ELEMENT} {label.WORKFLOWS}
                  </Button>
                  <Button
                    className={`ml-2 workflow-element-flow-btn ${dynamicFlow ? "wf-element-flow-btn-selected" : ""}`}
                    onClick={() => setDynamicFlow(true)}
                    selected={dynamicFlow}
                    togglable={true}
                    disabled={!props?.dataItem?.[editField]}
                    size='small'>
                    {icon.DYNAMIC_FLOW} {label.DYNAMIC}
                  </Button>
                </div>
                <div className={`form-group col-md-6`}>
                  <div className='d-flex justify-content-between align-items-end'>
                    <div>
                      <label className='w-100'>{label.FEATURES}</label>
                      <div className='d-flex align-items-center gap-2'>
                        <Button
                          selected={isApproval}
                          togglable={true}
                          size='small'
                          id='approval-btn'
                          className={`workflow-element-flow-btn ${isApproval ? "wf-element-flow-btn-selected" : ""}`}
                          title={tooltip.SEEK_APPROVAL}
                          disabled={!props?.dataItem?.[editField]}
                          onClick={props?.dataItem?.[editField] ? handleApprovalFlow : () => {}}>
                          <div className='d-flex align-items-center justify-content-between gap-1'>
                            <span>{!isApproval && !props?.dataItem?.[editField] && taskElement ? "" : icon?.APPROVAL_ICON}</span>
                            <span>{label.APPROVAL}</span>
                          </div>
                        </Button>
                        <Button
                          selected={isFlowLocked}
                          togglable={true}
                          size='small'
                          id='flow-element-lock-button'
                          ref={wrapper}
                          className={`workflow-element-flow-btn ${isFlowLocked ? "wf-element-flow-btn-selected" : ""}`}
                          title={isFlowLocked ? tooltip.LOCKED_ON_COMPLETION : tooltip.LOCK_TASK}
                          onClick={props?.dataItem?.[editField] ? handleFlowLock : () => {}}
                          onMouseOver={showTooltip}
                          disabled={!props?.dataItem?.[editField]}
                          onMouseLeave={hideTooltip}>
                          <div className='d-flex align-items-center justify-content-between gap-1'>
                            <span>{isFlowLocked ? icon.APPROVAL_LOCK : icon.UNLOCK}</span>
                            <span>{isFlowLocked ? label.TASK_LOCKED : label.LOCK_TASK}</span>
                          </div>
                        </Button>
                      </div>
                    </div>
                    <div>
                      <FlowElementCommandCell
                        {...props}
                        taskAssignmentTypes={taskAssignmentTypes}
                        workflowAssignmentTypes={workflowAssignmentTypes}
                        handleFlowElementEdit={handleFlowElementEdit}
                        handleNewFlowElement={handleNewFlowElement}
                        handleDeleteFlowElement={handleDeleteFlowElement}
                        dynamicFlow={dynamicFlow}
                        selectedActiveMember={selectedActiveMember}
                        profileType={profileType}
                        setPreviewAdvanced={setPreviewAdvanced}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            {!getPreviewAdvanced() && (
              <>
                {props?.dataItem?.[editField] ? (
                  <>
                    <div className='col-md-10'>
                      <Button
                        size='small'
                        onClick={() => {
                          setPreviewAdvanced(true);
                          return edit(props?.dataItem, flowElementData, setFlowElementData);
                        }}>
                        {label.ADVANCED}
                      </Button>
                    </div>
                    <div className='col-md-2'>
                      <FlowElementCommandCell
                        {...props}
                        taskAssignmentTypes={taskAssignmentTypes}
                        workflowAssignmentTypes={workflowAssignmentTypes}
                        handleFlowElementEdit={handleFlowElementEdit}
                        handleNewFlowElement={handleNewFlowElement}
                        handleDeleteFlowElement={handleDeleteFlowElement}
                        dynamicFlow={dynamicFlow}
                        selectedActiveMember={selectedActiveMember}
                        profileType={profileType}
                        setPreviewAdvanced={setPreviewAdvanced}
                      />
                    </div>
                  </>
                ) : (
                  <div className='col-md-12 d-flex justify-content-end'>
                    <FlowElementCommandCell
                      {...props}
                      taskAssignmentTypes={taskAssignmentTypes}
                      workflowAssignmentTypes={workflowAssignmentTypes}
                      handleFlowElementEdit={handleFlowElementEdit}
                      handleNewFlowElement={handleNewFlowElement}
                      handleDeleteFlowElement={handleDeleteFlowElement}
                      dynamicFlow={dynamicFlow}
                      selectedActiveMember={selectedActiveMember}
                      profileType={profileType}
                      setPreviewAdvanced={setPreviewAdvanced}
                    />
                  </div>
                )}
              </>
            )}
          </div>
          {!task?.taskId && !!isApproval && (
            <span className='approval-border-icon' title={tooltip.APPROVAL}>
              {icon.APPROVAL_ICON}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailedFlowElementDetails;
